/**
 * Divisional charts (vargas)
 *
 * The sixteen of the ṣoḍaśavarga — D-1 … D-60 — computed from **sidereal**
 * longitudes per classical Horā Ch. 6. Plus amśabala (BPHS / Parāśara §6.6) and
 * Vimśopaka Bala (BPHS weights over the four classical groups), and
 * vargottama (same rāśi in D-1 and D-9).
 *
 * ⚠ Do not generalise from D-9. Every varga's starting rule is read from the
 * source individually. A uniform `floor(deg / (30/n))` walk is right for only
 * about half of them.
 *
 * ⚠ Longitudes here are always sidereal. Tropical handed in lands ~24° off.
 *
 * © Manish Jagdish Thatte
 */

import {
  GRAHA_IDS, PHYSICAL_GRAHA_IDS, degreesInRashi, dignityOf, norm360,
  rashi, rashiIndexOf, rashiNthFrom
} from './constants.js';

/**
 * The sixteen of the ṣoḍaśavarga, in the order classical table 11 / §6.6.4 lists them.
 * D-5, D-6, D-8, D-11 exist in the classical text but are outside this group; they can be
 * added later without renumbering these.
 */
export const SHODASHA_VARGAS = Object.freeze([
  Object.freeze({ d: 1,  id: 'rasi',          name: 'Rāśi',            reads: 'the body and the whole life' }),
  Object.freeze({ d: 2,  id: 'hora',          name: 'Horā',            reads: 'wealth' }),
  Object.freeze({ d: 3,  id: 'drekkana',      name: 'Dreṣkāṇa',        reads: 'siblings and courage' }),
  Object.freeze({ d: 4,  id: 'chaturthamsa',  name: 'Turyāṁśa',        reads: 'fortune, property, home' }),
  Object.freeze({ d: 7,  id: 'saptamsa',      name: 'Saptāṁśa',        reads: 'children and grandchildren' }),
  Object.freeze({ d: 9,  id: 'navamsa',       name: 'Navāṁśa',         reads: 'dharma, spouse, the inner self' }),
  Object.freeze({ d: 10, id: 'dasamsa',       name: 'Daśāṁśa',         reads: 'career and public achievement' }),
  Object.freeze({ d: 12, id: 'dwadasamsa',    name: 'Dvādaśāṁśa',      reads: 'parents' }),
  Object.freeze({ d: 16, id: 'shodasamsa',    name: 'Ṣoḍaśāṁśa',       reads: 'vehicles, comforts and discomforts' }),
  Object.freeze({ d: 20, id: 'vimsamsa',      name: 'Viṁśāṁśa',        reads: 'religious and spiritual matters' }),
  Object.freeze({ d: 24, id: 'chaturvimsamsa', name: 'Caturviṁśāṁśa',  reads: 'learning and education' }),
  Object.freeze({ d: 27, id: 'bhamsa',        name: 'Bhāṁśa',          reads: 'strengths and weaknesses' }),
  Object.freeze({ d: 30, id: 'trimsamsa',     name: 'Triṁśāṁśa',       reads: 'evils, punishment, the sub-conscious' }),
  Object.freeze({ d: 40, id: 'khavedamsa',    name: 'Khavedāṁśa',      reads: 'auspicious and inauspicious events' }),
  Object.freeze({ d: 45, id: 'akshavedamsa',  name: 'Akṣavedāṁśa',     reads: 'all matters (finer)' }),
  Object.freeze({ d: 60, id: 'shashtyamsa',   name: 'Ṣaṣṭyāṁśa',       reads: 'past-life karma; finest grain — birth-time sensitive' })
]);

export const VARGA_BY_D = Object.freeze(
  Object.fromEntries(SHODASHA_VARGAS.map((v) => [v.d, v]))
);

/** D-60 span: 0°30′. A 4-second birth-time error can move this. */
export const SHASHTYAMSA_SPAN_DEG = 0.5;

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const odd = (i) => (i % 2) === 0; // 0-based: Meṣa=0 is odd (oja)

/** Part index 0..n-1 inside a rāśi, with the same boundary epsilon as divisions. */
function partIndex(degInRashi, n) {
  const span = 30 / n;
  // Boundary belongs to the later part (A5), except the exact 30° end clamps.
  const raw = degInRashi / span;
  const snapped = Math.floor(raw + 1e-9);
  return Math.min(n - 1, Math.max(0, snapped));
}

/** Count n rāśis from `from` (1-based nth: n=1 is itself). */
function nth(fromIndex, n) {
  return rashiNthFrom(fromIndex, n).index;
}

/**
 * Element → D-9 / D-27 style start (BPHS / Parāśara §6.2.9, §6.2.16).
 * Fire→Meṣa, Earth→Makara (D-9) or Karka (D-27), Air→Tulā, Water→Karka (D-9) or Makara (D-27).
 */
function startByTattva(rashiIndex, kind) {
  const t = rashi(rashiIndex).tattva;
  if (kind === 'navamsa') {
    return { agni: 0, bhu: 9, vayu: 6, jala: 3 }[t];
  }
  // D-27 Bhāṁśa: fiery Ar, earthy Cn, airy Li, watery Cp — BPHS / Parāśara §6.2.16.
  return { agni: 0, bhu: 3, vayu: 6, jala: 9 }[t];
}

function startByMobility(rashiIndex, movable, fixed, dual) {
  const m = rashi(rashiIndex).mobility;
  if (m === 'chara') return movable;
  if (m === 'sthira') return fixed;
  return dual;
}

/* ── Per-varga rāśi index ────────────────────────────────────────────────── */

/**
 * The rāśi index (0–11) a sidereal longitude occupies in divisional chart `d`.
 *
 * ◆ Every branch cites BPHS / Parāśara §6.2.x. Worked examples in that chapter are the
 * fixtures in `tests/jyotish/varga.test.js`.
 */
export function vargaRashiIndex(siderealLon, d) {
  if (!Number.isFinite(siderealLon)) {
    throw new TypeError('vargaRashiIndex: longitude must be finite');
  }
  const lon = norm360(siderealLon);
  const ri = rashiIndexOf(lon);
  const deg = degreesInRashi(lon);

  switch (d) {
    case 1:
      return ri;

    case 2: {
      // §6.2.2 — Sun's horā = Siṁha (4), Moon's = Karka (3). Not a walk.
      const firstHalf = deg < 15 - 1e-12;
      if (odd(ri)) return firstHalf ? 4 : 3; // odd: first Sun, second Moon
      return firstHalf ? 3 : 4;             // even: reversed
    }

    case 3: {
      // §6.2.3 — 1st / 5th / 9th from the rāśi.
      const p = partIndex(deg, 3);
      return nth(ri, [1, 5, 9][p]);
    }

    case 4: {
      // §6.2.4 — 1st / 4th / 7th / 10th.
      const p = partIndex(deg, 4);
      return nth(ri, [1, 4, 7, 10][p]);
    }

    case 7: {
      // §6.2.7 — odd: from itself; even: from 7th.
      const p = partIndex(deg, 7);
      const start = odd(ri) ? ri : nth(ri, 7);
      return nth(start, p + 1);
    }

    case 9: {
      // §6.2.9 — by tattva: Ar / Cp / Li / Cn.
      const p = partIndex(deg, 9);
      return nth(startByTattva(ri, 'navamsa'), p + 1);
    }

    case 10: {
      // §6.2.10 — odd: from itself; even: from 9th.
      const p = partIndex(deg, 10);
      const start = odd(ri) ? ri : nth(ri, 9);
      return nth(start, p + 1);
    }

    case 12: {
      // §6.2.12 — always from the rāśi itself.
      const p = partIndex(deg, 12);
      return nth(ri, p + 1);
    }

    case 16: {
      // §6.2.13 — movable Ar, fixed Le, dual Sg.
      const p = partIndex(deg, 16);
      const start = startByMobility(ri, 0, 4, 8);
      return nth(start, p + 1);
    }

    case 20: {
      // §6.2.14 — movable Ar, fixed Sg, dual Le.
      const p = partIndex(deg, 20);
      const start = startByMobility(ri, 0, 8, 4);
      return nth(start, p + 1);
    }

    case 24: {
      // §6.2.15 — odd from Le, even from Cn.
      const p = partIndex(deg, 24);
      const start = odd(ri) ? 4 : 3;
      return nth(start, p + 1);
    }

    case 27: {
      // §6.2.16 — by tattva: Ar / Cn / Li / Cp.
      const p = partIndex(deg, 27);
      return nth(startByTattva(ri, 'bhamsa'), p + 1);
    }

    case 30: {
      // §6.2.17 — unequal 5/5/8/7/5; odd and even use different sign lists.
      // Odd → Ar, Aq, Sg, Ge, Li; even → Ta, Vi, Pi, Cp, Sc.
      if (odd(ri)) {
        if (deg < 5) return 0;
        if (deg < 10) return 10;
        if (deg < 18) return 8;
        if (deg < 25) return 2;
        return 6;
      }
      if (deg < 5) return 1;
      if (deg < 12) return 5;
      if (deg < 20) return 11;
      if (deg < 25) return 9;
      return 7;
    }

    case 40: {
      // §6.2.18 — odd from Ar, even from Li.
      const p = partIndex(deg, 40);
      const start = odd(ri) ? 0 : 6;
      return nth(start, p + 1);
    }

    case 45: {
      // §6.2.19 — movable Ar, fixed Le, dual Sg.
      const p = partIndex(deg, 45);
      const start = startByMobility(ri, 0, 4, 8);
      return nth(start, p + 1);
    }

    case 60: {
      // §6.2.20 — 60 × 0°30′ from the rāśi itself.
      const p = partIndex(deg, 60);
      return nth(ri, p + 1);
    }

    default:
      throw new RangeError(
        `vargaRashiIndex: D-${d} is not in the ṣoḍaśavarga set ` +
        `(${SHODASHA_VARGAS.map((v) => v.d).join(', ')})`
      );
  }
}

/**
 * A synthetic longitude at the start of the varga rāśi — enough to feed chart
 * renderers that expect a longitude, not only an index. Degree-within-sign in
 * the varga is not classically defined for irregular vargas, so we place at 0°.
 */
export function vargaLongitude(siderealLon, d) {
  return vargaRashiIndex(siderealLon, d) * 30;
}

/* ── Chart assembly ──────────────────────────────────────────────────────── */

/**
 * Build one divisional chart from a kundali (or any `{ grahas, lagna? }`).
 *
 * Returns `{ d, meta, lagna, grahas, bhavas }` where each graha has
 * `{ id, rashiIndex, lon }` in the varga, and `bhavas` is twelve lists of
 * graha ids (whole-sign from the varga lagna when birth time is known).
 */
export function vargaChart(kundali, d) {
  const meta = VARGA_BY_D[d];
  if (!meta) throw new RangeError(`vargaChart: unknown D-${d}`);

  const grahas = {};
  for (const id of GRAHA_IDS) {
    const g = kundali.grahas?.[id];
    if (!g || !Number.isFinite(g.lon)) continue;
    const rashiIndex = vargaRashiIndex(g.lon, d);
    grahas[id] = { id, rashiIndex, lon: rashiIndex * 30, dignity: dignityOf(id, rashiIndex * 30) };
  }

  let lagna = null;
  if (kundali.lagna && Number.isFinite(kundali.lagna.lon)) {
    const rashiIndex = vargaRashiIndex(kundali.lagna.lon, d);
    lagna = { rashiIndex, lon: rashiIndex * 30 };
  }

  const bhavas = Array.from({ length: 12 }, () => []);
  if (lagna) {
    for (const g of Object.values(grahas)) {
      const bhavaNumber = ((g.rashiIndex - lagna.rashiIndex + 12) % 12) + 1;
      bhavas[bhavaNumber - 1].push(g.id);
    }
  }

  return {
    d,
    meta,
    lagna,
    grahas,
    bhavas,
    // D-60 warning is structural — the UI must surface it.
    birthTimeSensitive: d === 60
  };
}

/** All sixteen ṣoḍaśavarga charts for a kundali. */
export function allVargaCharts(kundali) {
  return SHODASHA_VARGAS.map((v) => vargaChart(kundali, v.d));
}

/* ── Vargottama ──────────────────────────────────────────────────────────── */

/** Same rāśi in D-1 and D-9. the classical text footnote to §16 / yoga literature. */
export function isVargottama(siderealLon) {
  return vargaRashiIndex(siderealLon, 1) === vargaRashiIndex(siderealLon, 9);
}

export function vargottama(kundali, grahaId) {
  const g = kundali.grahas?.[grahaId];
  if (!g || !Number.isFinite(g.lon)) return false;
  return isVargottama(g.lon);
}

/* ── Amśabala (BPHS / Parāśara §6.6) and Vimśopaka (BPHS weights) ────────────────────── */

const SHADVARGA_DS = [1, 2, 3, 9, 12, 30];
const SAPTAVARGA_DS = [1, 2, 3, 7, 9, 12, 30];
const DASAVARGA_DS = [1, 2, 3, 7, 9, 10, 12, 16, 30, 60];
const SHODASHAVARGA_DS = SHODASHA_VARGAS.map((v) => v.d);

/**
 * BPHS Vimśopaka weights. Totals are 20 in each scheme.
 * ⚠ BPHS / Parāśara §6.6 teaches *amśabala* (a count), not these weights. Both are
 * exported; the weighted form is what "Vimśopaka Bala" means in the plan.
 */
export const VIMSHOPAKA_WEIGHTS = Object.freeze({
  shadvarga: Object.freeze({ 1: 6, 2: 2, 3: 4, 9: 5, 12: 2, 30: 1 }),
  saptavarga: Object.freeze({ 1: 5, 2: 2, 3: 3, 7: 2.5, 9: 4.5, 12: 2, 30: 1 }),
  dasavarga: Object.freeze({
    1: 3, 2: 1.5, 3: 1.5, 7: 1.5, 9: 1.5, 10: 1.5, 12: 1.5, 16: 1.5, 30: 1.5, 60: 5
  }),
  shodashavarga: Object.freeze({
    1: 3.5, 2: 1, 3: 1, 4: 0.5, 7: 0.5, 9: 3, 10: 0.5, 12: 0.5,
    16: 2, 20: 0.5, 24: 0.5, 27: 0.5, 30: 1, 40: 0.5, 45: 0.5, 60: 4
  })
});

const GROUP_DS = Object.freeze({
  shadvarga: SHADVARGA_DS,
  saptavarga: SAPTAVARGA_DS,
  dasavarga: DASAVARGA_DS,
  shodashavarga: SHODASHAVARGA_DS
});

/** Is this dignity "good" for amśabala / vimśopaka credit? */
function isGoodDignity(label) {
  return label === 'exalted' || label === 'moolatrikona' || label === 'ownSign';
}

/**
 * Amśabala per BPHS / Parāśara §6.6 — count of charts in a group where the graha is in
 * mūlatrikoṇa, own sign, or exaltation. Returns the count and the classical
 * amśa name for that count.
 */
export function amsabala(kundali, grahaId, group = 'shodashavarga') {
  const ds = GROUP_DS[group];
  if (!ds) throw new RangeError(`amsabala: unknown group "${group}"`);
  const g = kundali.grahas?.[grahaId];
  if (!g || !Number.isFinite(g.lon)) {
    return { group, count: 0, amsa: null, charts: [] };
  }
  const charts = [];
  for (const d of ds) {
    const ri = vargaRashiIndex(g.lon, d);
    const dig = dignityOf(grahaId, ri * 30);
    if (isGoodDignity(dig.dignity)) charts.push(d);
  }
  return {
    group,
    count: charts.length,
    amsa: amsaName(group, charts.length),
    charts
  };
}

function amsaName(group, count) {
  const tables = {
    shadvarga: {
      2: 'kimsuka', 3: 'vyanjana', 4: 'chamara', 5: 'chatra', 6: 'kundala'
    },
    saptavarga: {
      2: 'kimsuka', 3: 'vyanjana', 4: 'chamara', 5: 'chatra', 6: 'kundala', 7: 'mukuta'
    },
    dasavarga: {
      2: 'parijata', 3: 'uttama', 4: 'gopura', 5: 'simhasana', 6: 'paravata',
      7: 'devaloka', 8: 'brahmaloka', 9: 'airavata', 10: 'sridharma'
    },
    shodashavarga: {
      2: 'bhedaka', 3: 'kusuma', 4: 'nagapurusha', 5: 'kanduka', 6: 'kerala',
      7: 'kalpavriksha', 8: 'chandanavana', 9: 'purnachandra', 10: 'ucchaisrava',
      11: 'dhanvantari', 12: 'suryakanta', 13: 'vidruma', 14: 'indrasana',
      15: 'goloka', 16: 'srivallabha'
    }
  };
  return tables[group][count] ?? null;
}

/**
 * Vimśopaka Bala for one graha under one weight scheme.
 *
 * Full credit (weight) when the graha is in exaltation / mūlatrikoṇa / own
 * sign in that varga; zero otherwise. (Finer proportional scales exist in some
 * traditions; this is the binary form that matches amśabala's notion of
 * "good" and keeps the four schemes comparable.)
 *
 * Returns `{ group, score, max, fraction, byVarga }`.
 */
export function vimshopakaBala(kundali, grahaId, group = 'shodashavarga') {
  const weights = VIMSHOPAKA_WEIGHTS[group];
  if (!weights) throw new RangeError(`vimshopakaBala: unknown group "${group}"`);
  const g = kundali.grahas?.[grahaId];
  const max = Object.values(weights).reduce((a, b) => a + b, 0);
  if (!g || !Number.isFinite(g.lon)) {
    return { group, score: 0, max, fraction: 0, byVarga: {} };
  }
  let score = 0;
  const byVarga = {};
  for (const [dStr, w] of Object.entries(weights)) {
    const d = Number(dStr);
    const ri = vargaRashiIndex(g.lon, d);
    const dig = dignityOf(grahaId, ri * 30);
    const credit = isGoodDignity(dig.dignity) ? w : 0;
    byVarga[d] = { weight: w, credit, dignity: dig.dignity, rashiIndex: ri };
    score += credit;
  }
  return { group, score, max, fraction: score / max, byVarga };
}

/** All four Vimśopaka schemes for every physical graha. */
export function allVimshopaka(kundali) {
  const out = {};
  for (const id of PHYSICAL_GRAHA_IDS) {
    out[id] = {};
    for (const group of Object.keys(VIMSHOPAKA_WEIGHTS)) {
      out[id][group] = vimshopakaBala(kundali, id, group);
    }
  }
  return out;
}
